import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EventCard } from '../components/predictions/EventCard'
import { PercentageValue } from '../components/ui/PercentageValue'
import { ProbabilityBar } from '../components/ui/ProbabilityBar'
import { categoryLabel, useLocale } from '../i18n/locale'
import { getPresentationDetail, probabilityExplanation, type PresentationDetail } from '../services/presentationService'

function changeCopy(prediction: PresentationDetail['prediction'], t: ReturnType<typeof useLocale>['t']) {
  if (prediction.change24h === undefined || prediction.previousProbability === undefined || prediction.probability === undefined) return t('detail.noCompleteChange')
  return `${Math.round(prediction.previousProbability * 100)}% → ${Math.round(prediction.probability * 100)}% · ${prediction.changeLabel}`
}
function sourceProbabilityLabel(value: number | undefined, t: ReturnType<typeof useLocale>['t']) {
  return value === undefined ? t('detail.noCurrentProbability') : t('detail.currentProbability', { value: Math.round(value * 100) })
}

export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const { locale, t } = useLocale()
  const [detail, setDetail] = useState<PresentationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    setDetail(null)
    if (!eventId) {
      setLoading(false)
      setError(t('detail.missingId'))
      return () => { active = false }
    }

    void getPresentationDetail(eventId, { locale })
      .then((nextDetail) => {
        if (!active) return
        setDetail(nextDetail)
        if (!nextDetail) setError(t('detail.unavailableTitle'))
      })
      .catch((reason) => {
        if (!active) return
        setError(reason instanceof Error ? reason.message : t('detail.readError'))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [eventId, locale])

  if (loading) return <div className="page-container event-detail-page"><div className="feed-state-panel" role="status" aria-live="polite"><p className="eyebrow">{t('detail.loadingEyebrow')}</p><h1>{t('detail.loadingTitle')}</h1><p>{t('detail.loadingBody')}</p></div></div>

  if (!detail) return <div className="page-container event-detail-page"><div className="feed-state-panel feed-state-panel-error" role="alert"><p className="eyebrow">{t('detail.unavailableEyebrow')}</p><h1>{t('detail.unavailableTitle')}</h1><p>{error ?? t('detail.notFound')}</p><Link className="button button-secondary" to="/">{t('detail.backToday')}</Link></div></div>

  const { prediction, related } = detail
  const binary = prediction.isBinary
  return (
    <div className="page-container event-detail-page">
      <div className="detail-back-row"><Link to="/" className="detail-back-link">← {t('detail.backToday')}</Link><span>{t('detail.eyebrow', { category: categoryLabel(prediction.category, locale) })}</span></div>

      <section className="detail-hero" aria-labelledby="event-detail-title">
        <div className="detail-hero-copy">
          <div className="detail-meta-row">
            <span className={`category-tag category-tag-${prediction.category}`}>{categoryLabel(prediction.category, locale)}</span>
            {prediction.temporalStatus === 'ended' && <span className="source-chip">{t('detail.ended')}</span>}
            <span className="source-chip">{prediction.sourceSummary}</span>
          </div>
          <h1 id="event-detail-title">{prediction.displayTitle}</h1>
          <p className="detail-interpretation">{binary ? probabilityExplanation(prediction.probability, locale) : t('detail.marketInterpretation')}</p>
        </div>
        <div className="detail-judgement" aria-label={t('detail.currentMarketJudgement')}>
          {binary ? prediction.probability === undefined ? <strong className="probability-missing">{t('card.noProbability')}</strong> : <PercentageValue value={prediction.probability} element="strong" /> : <div className="detail-distribution">{prediction.outcomes.slice(0, 4).map((outcome) => <div key={outcome.id}>{outcome.probability === undefined ? <strong className="probability-missing">{t('card.noProbability')}</strong> : <PercentageValue value={outcome.probability} element="strong" />}<span>{outcome.displayOutcomeLabel}</span></div>)}</div>}
          <span>{binary ? t('detail.currentMarketProbability') : prediction.outcomeStructureLabel}</span>
          {!binary && prediction.outcomeStructureNote && <small className="detail-distribution-note">{prediction.outcomeStructureNote}</small>}
          {binary && prediction.probability !== undefined && <ProbabilityBar probability={prediction.probability} />}
        </div>
      </section>

      <div className="detail-layout">
        <div className="detail-main">
          <section className="detail-block" aria-labelledby="detail-today-title"><p className="eyebrow">{t('detail.todaySignal')}</p><h2 id="detail-today-title">{t('detail.todayWhy')}</h2><p className="detail-lead">{prediction.todayReason ?? t('card.noVerifiedChange')}</p><div className="detail-change-readout"><span>{t('detail.changeRecord')}</span><strong>{changeCopy(prediction, t)}</strong></div></section>
          <section className="detail-block" aria-labelledby="detail-importance-title"><p className="eyebrow">{t('detail.informationValue')}</p><h2 id="detail-importance-title">{t('detail.whyImportant')}</h2><p className="detail-lead">{prediction.importanceReason}</p></section>
          <section className="detail-block" aria-labelledby="detail-sources-title"><p className="eyebrow">{t('detail.publicSources')}</p><h2 id="detail-sources-title">{t('detail.predictionSources')}</h2><div className="detail-source-list">{prediction.sources.map((source) => <div className="detail-source-row" key={source.name}><div><strong>{source.name}</strong><span>{t('common.synced')}</span></div><b>{sourceProbabilityLabel(source.probability, t)}</b>{source.sourceUrl && <a href={source.sourceUrl} target="_blank" rel="noreferrer">{t('detail.viewSource')}</a>}</div>)}</div>{prediction.consensusLabel && <p className="detail-consensus-note">{t('detail.sourceNote', { label: prediction.consensusLabel })}</p>}</section>
          {related.length > 0 && <section className="detail-block" aria-labelledby="detail-related-title"><p className="eyebrow">{t('detail.relatedEyebrow')}</p><h2 id="detail-related-title">{t('detail.relatedTitle')}</h2><p className="detail-related-intro">{t('detail.relatedIntro')}</p><div className="detail-related-grid">{related.map((item) => <EventCard key={item.eventId} prediction={item} variant="compact" />)}</div></section>}
        </div>

        <aside className="detail-aside">
          <details className="probability-education" open><summary>{t('detail.probabilitySummary')}</summary><p>{t('detail.probabilityBody')}</p></details>
          <div className="detail-fact-card"><span className="presentation-label">{t('detail.resultTime')}</span><strong>{prediction.deadlineLabel}</strong><span>{t('detail.resultTimeBody')}</span></div>
          <div className="detail-fact-card detail-fact-card-muted"><span className="presentation-label">{t('detail.dataBoundary')}</span><strong>{t('detail.noSyntheticHistory')}</strong><span>{t('detail.dataBoundaryBody')}</span></div>
        </aside>
      </div>
    </div>
  )
}
